<script lang="ts" setup>
import { useIntervalFn, useThrottleFn } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

const {
	salt,
} = defineProps<{
	/**
	 * Reactive value that's watched to pick a new random verb to show.
	 * This is used in conjunction with the block count to show a different random
	 * verb every time we start rendering a new block
	 */
	salt?: number;
}>();

/**
 * How long to show each frame of the spinner
 */
const SPINNER_INTERVAL = 80;

/**
 * How many verbs there are to choose from in en-US.yaml
 * @see #layers/ai/i18n/locales/en-US.yaml
 */
const VERB_CHOICES = 20;

/**
 * How long a verb has to be on screen at minimum before it's changed again.
 * This avoids verbs flashing by rapidly and being distracting
 */
const VERB_THROTTLE = 2000;

const pickVerb = (previous?: number) => {
	let index = previous;

	while (index === previous) {
		index = Math.floor(Math.random() * VERB_CHOICES);
	}

	return index;
};

const verbIndex = ref(pickVerb());
const verbTranslationKey = computed(() => `ai.spinner_verbs.verb_${verbIndex.value}`);

const changeVerb = useThrottleFn(() => {
	verbIndex.value = pickVerb();
}, VERB_THROTTLE, false);

watch(
	() => salt,
	() => changeVerb(),
);

const spinners = [
	['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'],
	['⠋', '⠙', '⠚', '⠞', '⠖', '⠦', '⠴', '⠲', '⠳', '⠓'],
	['⠄', '⠆', '⠇', '⠋', '⠙', '⠸', '⠰', '⠠', '⠰', '⠸', '⠙', '⠋', '⠇', '⠆'],
	['⠋', '⠙', '⠚', '⠒', '⠂', '⠂', '⠒', '⠲', '⠴', '⠦', '⠖', '⠒', '⠐', '⠐', '⠒', '⠓', '⠋'],
	['⠁', '⠉', '⠙', '⠚', '⠒', '⠂', '⠂', '⠒', '⠲', '⠴', '⠤', '⠄', '⠄', '⠤', '⠴', '⠲', '⠒', '⠂', '⠂', '⠒', '⠚', '⠙', '⠉', '⠁'],
	['⠈', '⠉', '⠋', '⠓', '⠒', '⠐', '⠐', '⠒', '⠖', '⠦', '⠤', '⠠', '⠠', '⠤', '⠦', '⠖', '⠒', '⠐', '⠐', '⠒', '⠓', '⠋', '⠉', '⠈'],
	['⠁', '⠁', '⠉', '⠙', '⠚', '⠒', '⠂', '⠂', '⠒', '⠲', '⠴', '⠤', '⠄', '⠄', '⠤', '⠠', '⠠', '⠤', '⠦', '⠖', '⠒', '⠐', '⠐', '⠒', '⠓', '⠋', '⠉', '⠈', '⠈'],
] as const;

const spinner = spinners[Math.floor(Math.random() * spinners.length)]!;
const currentIndex = ref(0);

useIntervalFn(() => {
	currentIndex.value = (currentIndex.value + 1) % spinner.length;
}, SPINNER_INTERVAL);
</script>

<template>
	<div class="flex items-center gap-2">
		<span class="font-mono text-sm select-none">{{ spinner[currentIndex] }}</span>
		<DChatShimmer class="text-xs" :text="$t(verbTranslationKey)" />
	</div>
</template>
